import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/react';
import { env } from '@/config/env';

const SENSITIVE_URL_PARAMS = new Set([
  'access_token',
  'apikey',
  'code',
  'password',
  'refresh_token',
  'token',
  'token_hash',
]);

function shouldDropEvent(event: Sentry.Event): boolean {
  const message = event.exception?.values?.[0]?.value ?? event.message ?? '';
  if (typeof message === 'string' && message.includes('ResizeObserver loop')) {
    return true;
  }

  return false;
}

function redactSensitiveSearchParams(params: URLSearchParams): void {
  for (const key of [...params.keys()]) {
    if (SENSITIVE_URL_PARAMS.has(key.toLowerCase())) {
      params.set(key, '[Filtered]');
    }
  }
}

function sanitizeQueryString(queryString: string): string {
  const normalized = queryString.startsWith('?') ? queryString.slice(1) : queryString;
  if (!normalized) {
    return '';
  }

  const params = new URLSearchParams(normalized);
  redactSensitiveSearchParams(params);
  const sanitized = params.toString();
  return sanitized ? `?${sanitized}` : '';
}

function looksLikeTelemetryUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  return (
    /^https?:\/\//i.test(trimmed) ||
    trimmed.startsWith('/') ||
    trimmed.startsWith('//')
  );
}

export function sanitizeTelemetryUrl(url: string): string {
  try {
    const parsed = new URL(
      url,
      typeof window !== 'undefined' ? window.location.origin : 'https://localhost',
    );
    redactSensitiveSearchParams(parsed.searchParams);

    if (parsed.hash.length > 1) {
      const hashParams = new URLSearchParams(parsed.hash.slice(1));
      redactSensitiveSearchParams(hashParams);
      const sanitizedHash = hashParams.toString();
      parsed.hash = sanitizedHash ? `#${sanitizedHash}` : '';
    }

    return parsed.toString();
  } catch {
    return '[Filtered URL]';
  }
}

export function sanitizeSentryEvent<T extends Sentry.Event>(event: T): T {
  if (event.request?.url) {
    event.request.url = sanitizeTelemetryUrl(event.request.url);
  }

  if (typeof event.request?.query_string === 'string') {
    event.request.query_string = sanitizeQueryString(event.request.query_string);
  }

  const headers = event.request?.headers;
  if (headers?.Referer) {
    headers.Referer = sanitizeTelemetryUrl(headers.Referer);
  }
  if (headers?.referer) {
    headers.referer = sanitizeTelemetryUrl(headers.referer);
  }

  if (event.breadcrumbs) {
    for (const breadcrumb of event.breadcrumbs) {
      if (breadcrumb.data?.url) {
        breadcrumb.data.url = sanitizeTelemetryUrl(String(breadcrumb.data.url));
      }
      if (breadcrumb.data?.to) {
        breadcrumb.data.to = sanitizeTelemetryUrl(String(breadcrumb.data.to));
      }
      if (breadcrumb.data?.from) {
        breadcrumb.data.from = sanitizeTelemetryUrl(String(breadcrumb.data.from));
      }
    }
  }

  if (event.spans) {
    for (const span of event.spans) {
      if (span.description && looksLikeTelemetryUrl(span.description)) {
        span.description = sanitizeTelemetryUrl(span.description);
      }
      if (span.data?.url) {
        span.data.url = sanitizeTelemetryUrl(String(span.data.url));
      }
    }
  }

  return event;
}

export function initSentry(): void {
  // Sanitize credentials only in outbound Sentry payloads. Do not rewrite
  // window.location here: AuthConfirm reads token_hash from the URL on mount
  // and strips it after capture in its own effect.
  const dsn = env.sentryDsn?.trim();
  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: env.sentryEnvironment,
    release: env.appVersion?.trim() || undefined,
    // setupGlobalErrorHandlers routes window errors through logError with typed tags.
    integrations: (defaultIntegrations) => [
      ...defaultIntegrations.filter(
        (integration) => integration.name !== 'GlobalHandlers',
      ),
      browserTracingIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend(event) {
      if (shouldDropEvent(event)) {
        return null;
      }

      return sanitizeSentryEvent(event);
    },
    beforeSendTransaction(event) {
      return sanitizeSentryEvent(event);
    },
  });
}

export function captureSentryException(
  error: Error,
  context?: {
    errorType?: string;
    severity?: string;
    componentStack?: string;
    metadata?: Record<string, unknown>;
  },
): void {
  if (!env.sentryDsn?.trim()) {
    return;
  }

  Sentry.withScope((scope) => {
    if (context?.errorType) {
      scope.setTag('error_type', context.errorType);
    }

    if (context?.severity) {
      scope.setTag('severity', context.severity);
    }

    if (context?.componentStack) {
      scope.setExtra('component_stack', context.componentStack);
    }

    if (context?.metadata) {
      scope.setContext('metadata', context.metadata);
    }

    Sentry.captureException(error);
  });
}
